import React from 'react';
import { Document, Page, Text, View } from '@react-pdf/renderer';
import { reportPdfStyles as styles } from '../../../Styles/reportPdfStyles';

interface MarketingReportData {
  dateRange: string;
  kpis: {
    totalLeads: number;
    finishedLeads: number;
    conversionRate: number;
    leadTemplates: number;
  };
  leadConversion?: Array<{ status: string; count: number; percentage: number; }>;
  campaignPerformance?: Array<{ campaign: string; leads: number; conversion: number; }>;
  clientSegmentationByCategory?: Array<{ category: string; count: number; percentage: number; }>;
  clientSegmentationByPeriodicity?: Array<{ periodicity: string; count: number; percentage: number; }>;
  agentPerformance?: Array<{ agent: string; contacts: number; leads: number; }>;
  agentConversionRate?: Array<{ agent: string; conversionRate: number; }>;
}

interface MarketingPDFReportProps {
  data: MarketingReportData;
}

const MarketingPDFReport: React.FC<MarketingPDFReportProps> = ({ data }) => {
  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('ro-RO').format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.title}>Marketing Report</Text>
          <Text style={styles.subtitle}>Cafe-Mag CRM</Text>
          <Text style={styles.dateRange}>Period: {data.dateRange}</Text>
        </View>

        {/* KPIs */}
        <View style={styles.kpiSection}>
          <View style={styles.kpiCard}>
            <Text style={styles.kpiLabel}>Total Leads</Text>
            <Text style={styles.kpiValue}>{formatNumber(data.kpis.totalLeads)}</Text>
          </View>
          <View style={styles.kpiCard}>
            <Text style={styles.kpiLabel}>Finished Leads</Text>
            <Text style={styles.kpiValue}>{formatNumber(data.kpis.finishedLeads)}</Text>
          </View>
          <View style={styles.kpiCard}>
            <Text style={styles.kpiLabel}>Conversion Rate</Text>
            <Text style={styles.kpiValue}>{formatPercentage(data.kpis.conversionRate)}</Text>
          </View>
          <View style={styles.kpiCard}>
            <Text style={styles.kpiLabel}>Lead Templates</Text>
            <Text style={styles.kpiValue}>{formatNumber(data.kpis.leadTemplates)}</Text>
          </View>
        </View>

        {/* Lead Conversion */}
        {data.leadConversion && data.leadConversion.length > 0 && (
          <View>
            <Text style={styles.sectionTitle}>Lead Conversion by Status</Text>
            <View style={styles.table}>
              <View style={styles.tableHeader}>
                <Text style={styles.tableCellHeader}>Status</Text>
                <Text style={styles.tableCellHeader}>Count</Text>
                <Text style={styles.tableCellHeader}>Percentage</Text>
              </View>
              {data.leadConversion.map((item, index) => (
                <View key={index} style={styles.tableRow}>
                  <Text style={styles.tableCell}>{item.status}</Text>
                  <Text style={styles.tableCell}>{formatNumber(item.count)}</Text>
                  <Text style={styles.tableCell}>{formatPercentage(item.percentage)}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Campaign Performance */}
        {data.campaignPerformance && data.campaignPerformance.length > 0 && (
          <View>
            <Text style={styles.sectionTitle}>Campaign Performance</Text>
            <View style={styles.table}>
              <View style={styles.tableHeader}>
                <Text style={styles.tableCellHeader}>Campaign</Text>
                <Text style={styles.tableCellHeader}>Leads</Text>
                <Text style={styles.tableCellHeader}>Conversion Rate</Text>
              </View>
              {data.campaignPerformance.map((item, index) => (
                <View key={index} style={styles.tableRow}>
                  <Text style={styles.tableCell}>{item.campaign}</Text>
                  <Text style={styles.tableCell}>{formatNumber(item.leads)}</Text>
                  <Text style={styles.tableCell}>{formatPercentage(item.conversion)}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Client Segmentation by Category */}
        {data.clientSegmentationByCategory && data.clientSegmentationByCategory.length > 0 && (
          <View>
            <Text style={styles.sectionTitle}>Client Segmentation by Category</Text>
            <View style={styles.table}>
              <View style={styles.tableHeader}>
                <Text style={styles.tableCellHeader}>Category</Text>
                <Text style={styles.tableCellHeader}>Count</Text>
                <Text style={styles.tableCellHeader}>Percentage</Text>
              </View>
              {data.clientSegmentationByCategory.map((item, index) => (
                <View key={index} style={styles.tableRow}>
                  <Text style={styles.tableCell}>{item.category}</Text>
                  <Text style={styles.tableCell}>{formatNumber(item.count)}</Text>
                  <Text style={styles.tableCell}>{formatPercentage(item.percentage)}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Client Segmentation by Periodicity */}
        {data.clientSegmentationByPeriodicity && data.clientSegmentationByPeriodicity.length > 0 && (
          <View>
            <Text style={styles.sectionTitle}>Client Segmentation by Periodicity</Text>
            <View style={styles.table}>
              <View style={styles.tableHeader}>
                <Text style={styles.tableCellHeader}>Periodicity</Text>
                <Text style={styles.tableCellHeader}>Count</Text>
                <Text style={styles.tableCellHeader}>Percentage</Text>
              </View>
              {data.clientSegmentationByPeriodicity.map((item, index) => (
                <View key={index} style={styles.tableRow}>
                  <Text style={styles.tableCell}>{item.periodicity}</Text>
                  <Text style={styles.tableCell}>{formatNumber(item.count)}</Text>
                  <Text style={styles.tableCell}>{formatPercentage(item.percentage)}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Agent Performance */}
        {data.agentPerformance && data.agentPerformance.length > 0 && (
          <View>
            <Text style={styles.sectionTitle}>Agent Performance</Text>
            <View style={styles.table}>
              <View style={styles.tableHeader}>
                <Text style={styles.tableCellHeader}>Agent</Text>
                <Text style={styles.tableCellHeader}>Contacts</Text>
                <Text style={styles.tableCellHeader}>Leads</Text>
              </View>
              {data.agentPerformance.map((item, index) => (
                <View key={index} style={styles.tableRow}>
                  <Text style={styles.tableCell}>{item.agent}</Text>
                  <Text style={styles.tableCell}>{formatNumber(item.contacts)}</Text>
                  <Text style={styles.tableCell}>{formatNumber(item.leads)}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Agent Conversion Rate */}
        {data.agentConversionRate && data.agentConversionRate.length > 0 && (
          <View>
            <Text style={styles.sectionTitle}>Agent Conversion Rate</Text>
            <View style={styles.table}>
              <View style={styles.tableHeader}>
                <Text style={styles.tableCellHeader}>Agent</Text>
                <Text style={styles.tableCellHeader}>Conversion Rate</Text>
              </View>
              {data.agentConversionRate.map((item, index) => (
                <View key={index} style={styles.tableRow}>
                  <Text style={styles.tableCell}>{item.agent}</Text>
                  <Text style={styles.tableCell}>{formatPercentage(item.conversionRate)}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Footer */}
        <Text style={styles.footer} fixed>
          Generated by Cafe-Mag CRM - {new Date().toLocaleDateString('ro-RO')}
        </Text>
      </Page>
    </Document>
  );
};

export default MarketingPDFReport;
